import React from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useSortable} from '@dnd-kit/sortable';
import * as css from './LectureCard.css';
import AssignedTicketsButton from './assignedTicketsButton/AssignedTicketsButton';
import LectureSticker from './lectureSticker/LectureSticker';
import LecturersDisplay from './lecturersDisplay/LecturersDisplay';
import LectureCardMenu from './lectureCardMenu/LectureCardMenu';
import DragIndicator from '../../../../assets/icons/DragIndicator';
import {formatNumber} from '../../../../library/numberFunction';
import {TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {RootState} from '../../../../redux/store.type';

interface LectureCardProps {
    lecture: TTimetableItem;
    index?: number;
    onTicketsClick?: () => void;
    onEdit?: () => void;
    expanded?: boolean;
}

function LectureCard({
    lecture,
    index = 0,
    onTicketsClick,
    onEdit,
    expanded = false,
}: LectureCardProps) {
    const {t} = useTranslation('timetable');
    const currencySymbol = useSelector((state: RootState) => state.event.currencySymbol);
    const {title, lecturers = [], isBreak, price, amount, tickets = [], place} = lecture;

    const {attributes, listeners, setNodeRef, transform, isDragging} = useSortable({
        id: lecture.ttid,
        data: {index},
    });
    const dragVars = {
        '--dnd-transform': transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : 'none',
    } as React.CSSProperties;

    return (
        <css.container $expanded={expanded} $isDragging={isDragging} ref={setNodeRef} style={dragVars} onDoubleClick={onEdit} {...attributes}>
            <css.dragHandle {...listeners}>
                <css.dragIcon><DragIndicator /></css.dragIcon>
            </css.dragHandle>
            <LectureSticker index={index} expanded={expanded} />
            <css.content $expanded={expanded}>
                <css.details>
                    <css.title $expanded={expanded}>{title}</css.title>
                    {expanded ? (
                        <>
                            <LecturersDisplay lecturers={lecturers} isBreak={isBreak} />
                            <css.ticketInfo>
                                <css.ticketDetails>
                                    {price != null && (
                                        <css.detailText><css.detailLabel>{t('additionalPrice')}</css.detailLabel>{formatNumber(price)} {currencySymbol}</css.detailText>
                                    )}
                                    {amount != null && (
                                        <css.detailText><css.detailLabel>{t('seatsCount')}</css.detailLabel>{formatNumber(amount)}</css.detailText>
                                    )}
                                    <css.detailText><css.detailLabel>{t('location')}</css.detailLabel>{place}</css.detailText>
                                </css.ticketDetails>
                                <AssignedTicketsButton
                                    count={tickets.length}
                                    onClick={onTicketsClick}
                                />
                            </css.ticketInfo>
                        </>
                    ) : (
                        <LecturersDisplay lecturers={lecturers} isBreak={isBreak} />
                    )}
                </css.details>
                <LectureCardMenu
                    lecture={lecture}
                    onEdit={onEdit}
                />
            </css.content>
        </css.container>
    );
}

export default LectureCard;
